import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";
import Topbar from "../components/Topbar";
import { CheckCircle2, Camera, Plus, ShieldCheck, X, Image as ImageIcon, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoSrc = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

export default function Audits() {
  const { currentWorkspaceId } = useWorkspace();
  const [audits, setAudits] = useState([]);
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newAudit, setNewAudit] = useState({ name: "", template: "VORION_SAFETY" });
  const fileInputRef = useRef(null);

  const reload = () => {
    if (!currentWorkspaceId) return;
    api.get("/audits", { params: { workspace_id: currentWorkspaceId } })
      .then((r) => {
        setAudits(r.data);
        if (active) {
          const fresh = r.data.find((a) => a.id === active.id);
          if (fresh) setActive(fresh);
        }
      });
  };

  useEffect(reload, [currentWorkspaceId]);

  const createAudit = async () => {
    await api.post("/audits", newAudit, { params: { workspace_id: currentWorkspaceId } });
    setShowNew(false);
    setNewAudit({ name: "", template: "VORION_SAFETY" });
    reload();
  };

  const toggleItem = async (auditId, itemId, currentDone) => {
    await api.patch(`/audits/${auditId}/items/${itemId}`, { completed: !currentDone });
    reload();
  };

  const setPhoto = (auditId, itemId) => {
    fileInputRef.current = { auditId, itemId };
    document.getElementById(`audit-photo-input-${itemId}`)?.click();
  };

  const onPhotoChosen = async (e, auditId, itemId) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/audits/${auditId}/items/${itemId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Photo upload failed";
      alert(msg);
    }
  };

  const removePhoto = async (auditId, itemId) => {
    if (!window.confirm("Remove photo evidence?")) return;
    await api.patch(`/audits/${auditId}/items/${itemId}`, { photo_url: null });
    reload();
  };

  const submit = async (id) => {
    if (!window.confirm("Submit this audit? It will be locked.")) return;
    await api.post(`/audits/${id}/submit`);
    reload();
  };

  const progress = (a) => {
    if (!a.items.length) return 0;
    return (a.items.filter((i) => i.completed).length / a.items.length) * 100;
  };

  return (
    <div data-testid="audits-page">
      <Topbar
        title="Audits"
        subtitle="Vorion Safety & compliance checklists with photo evidence"
        breadcrumb="Operations"
        right={
          <button onClick={() => setShowNew(true)} data-testid="new-audit-btn" className="flex items-center gap-1.5 bg-[#D4A968] hover:bg-[#E8C285] text-black px-3 py-2 rounded-md text-sm font-semibold">
            <Plus className="w-4 h-4" /> New audit
          </button>
        }
      />
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div data-testid="audit-list">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-[#10B981]" />
            Audits ({audits.length})
          </h3>
          <div className="space-y-2">
            {audits.map((a) => (
              <button
                key={a.id}
                onClick={() => setActive(a)}
                data-testid={`audit-card-${a.id}`}
                className={`w-full text-left bg-[#141417] border rounded-lg p-3 transition-colors ${
                  active?.id === a.id ? "border-[#D4A968]" : "border-zinc-800 hover:border-zinc-600"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white font-semibold">{a.name}</span>
                  <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded border ${
                    a.status === "submitted"
                      ? "bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]"
                      : "bg-[#F59E0B]/10 border-[#F59E0B]/30 text-[#F59E0B]"
                  }`}>{a.status}</span>
                </div>
                <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mt-1">{a.template}</div>
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] font-mono mb-1 text-zinc-500">
                    <span>Progress</span>
                    <span className="text-white">{a.items.filter((i) => i.completed).length}/{a.items.length}</span>
                  </div>
                  <div className="h-1.5 bg-[#0B0B0E] rounded overflow-hidden">
                    <div className="h-full bg-[#10B981]" style={{ width: `${progress(a)}%` }} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {active && (
          <div className="bg-[#141417] border border-zinc-800 rounded-lg p-4" data-testid="audit-detail">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-white">{active.name}</h3>
                <div className="text-[10px] uppercase font-mono tracking-widest text-zinc-500">{active.template}</div>
              </div>
              {active.status !== "submitted" && (
                <button
                  onClick={() => submit(active.id)}
                  data-testid="submit-audit-btn"
                  className="bg-[#10B981] hover:bg-[#059669] text-white px-3 py-1.5 rounded text-xs font-semibold"
                >
                  Submit audit
                </button>
              )}
            </div>
            <div className="space-y-2">
              {active.items.map((it) => (
                <div key={it.id} data-testid={`audit-item-${it.id}`} className="bg-[#0B0B0E] border border-zinc-800 rounded p-3">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleItem(active.id, it.id, it.completed)}
                      className={`mt-0.5 ${it.completed ? "text-[#10B981]" : "text-zinc-600 hover:text-[#D4A968]"}`}
                      data-testid={`audit-toggle-${it.id}`}
                    >
                      <CheckCircle2 className={`w-4 h-4 ${it.completed ? "fill-[#10B981]/20" : ""}`} />
                    </button>
                    <div className="flex-1">
                      <div className={`text-sm ${it.completed ? "text-zinc-400 line-through" : "text-white"}`}>{it.text}</div>
                      {it.photo_url && (
                        <a href={photoSrc(it.photo_url)} target="_blank" rel="noreferrer" data-testid={`audit-photo-${it.id}`} className="block mt-1.5">
                          <img src={photoSrc(it.photo_url)} alt="evidence" className="max-h-32 rounded border border-zinc-800" />
                        </a>
                      )}
                    </div>
                    <input
                      type="file"
                      id={`audit-photo-input-${it.id}`}
                      data-testid={`audit-photo-input-${it.id}`}
                      accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                      style={{ display: "none" }}
                      onChange={(e) => onPhotoChosen(e, active.id, it.id)}
                    />
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => setPhoto(active.id, it.id)}
                        data-testid={`audit-photo-btn-${it.id}`}
                        disabled={active.status === "submitted"}
                        className="flex items-center gap-1 text-[10px] uppercase font-mono tracking-widest text-zinc-500 hover:text-[#D4A968] border border-zinc-800 hover:border-[#D4A968] rounded px-1.5 py-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {it.photo_url ? <ImageIcon className="w-3 h-3" /> : <Camera className="w-3 h-3" />}
                        {it.photo_url ? "Replace" : "Photo"}
                      </button>
                      {it.photo_url && active.status !== "submitted" && (
                        <button
                          onClick={() => removePhoto(active.id, it.id)}
                          data-testid={`audit-photo-remove-${it.id}`}
                          className="flex items-center gap-1 text-[10px] uppercase font-mono tracking-widest text-zinc-500 hover:text-[#EF4444] border border-zinc-800 hover:border-[#EF4444] rounded px-1.5 py-0.5"
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showNew && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="new-audit-modal" className="bg-[#0B0B0E] border border-zinc-800 rounded-lg p-5 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">New audit</h3>
              <button onClick={() => setShowNew(false)}><X className="w-4 h-4 text-zinc-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-1">Name</label>
                <input
                  value={newAudit.name}
                  onChange={(e) => setNewAudit({ ...newAudit, name: e.target.value })}
                  data-testid="new-audit-name"
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2.5 py-2 text-sm text-white outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-1">Template</label>
                <select
                  value={newAudit.template} onChange={(e) => setNewAudit({ ...newAudit, template: e.target.value })}
                  data-testid="new-audit-template"
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2.5 py-2 text-sm text-white outline-none"
                >
                  <option value="VORION_SAFETY">Vorion Safety (12 items)</option>
                </select>
              </div>
              <button onClick={createAudit} data-testid="new-audit-submit" className="w-full bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold py-2 rounded">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
